// /admin/:slug/pilot/:userId — pilot detail with verify / reject actions.

import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Section, Cell, List, Button, Caption, Spinner, Banner, Input, Textarea,
} from "@telegram-apps/telegram-ui";
import { api, openAuthedFile } from "../lib/api.js";
import { usePageBack } from "../lib/telegram.js";

const KIND_LABELS = { fasr_id: "ID ФАСР", insurance: "Страховка" };

const DOC_STATUS_META = {
  pending:  { tone: "warn",   text: "⏳ Ожидает" },
  verified: { tone: "ok",     text: "✓ Подтверждён" },
  rejected: { tone: "danger", text: "✕ Отклонён" },
};

export default function AdminPilot() {
  const { slug, userId } = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const fromPilots = search.get("from_pilots") === "1";
  const backTo = fromPilots
    ? `/admin/${slug}/pilots`
    : search.get("from")
      ? `/admin/${slug}?date=${search.get("from")}`
      : `/admin/${slug}`;
  const backLabel = fromPilots ? "← К списку пилотов" : "← К списку броней";
  usePageBack(backTo);

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [dmOpen, setDmOpen] = useState(false);
  const [dmText, setDmText] = useState("");

  function load() {
    api(`/api/v1/admin/airfields/${slug}/pilots/${userId}`)
      .then(setData)
      .catch((e) => setError(`Не удалось загрузить пилота: ${e.message}`));
  }
  useEffect(load, [slug, userId]);

  async function setVerification(status, note, until) {
    setBusy(true);
    setFeedback(null);
    try {
      await api(`/api/v1/admin/airfields/${slug}/pilots/${userId}/verification`, {
        method: "POST",
        body: JSON.stringify({ status, note, until }),
      });
      setFeedback({ kind: "ok", text: `Статус: ${status}` });
      load();
    } catch (e) {
      setFeedback({ kind: "err", text: e.message || "Ошибка" });
    } finally {
      setBusy(false);
    }
  }

  async function sendDm() {
    const body = dmText.trim();
    if (body.length === 0) return;
    setBusy(true);
    setFeedback(null);
    try {
      await api(`/api/v1/admin/airfields/${slug}/pilots/${userId}/dm`, {
        method: "POST",
        body: JSON.stringify({ text: body }),
      });
      setFeedback({ kind: "ok", text: "Сообщение отправлено." });
      setDmText("");
      setDmOpen(false);
    } catch (e) {
      setFeedback({ kind: "err", text: e.message || "Не удалось отправить." });
    } finally {
      setBusy(false);
    }
  }

  async function reviewDoc(docId, status, reason) {
    setBusy(true);
    setFeedback(null);
    try {
      await api(`/api/v1/admin/airfields/${slug}/documents/${docId}/review`, {
        method: "POST",
        body: JSON.stringify({ status, reason }),
      });
      setFeedback({ kind: "ok", text: `Документ #${docId}: ${status}` });
      load();
    } catch (e) {
      setFeedback({ kind: "err", text: e.message || "Ошибка" });
    } finally {
      setBusy(false);
    }
  }

  if (error && !data) return <List><Banner type="section" header="Ошибка">{error}</Banner></List>;
  if (!data) return <List><Section><Cell before={<Spinner size="m" />}>Загружаем…</Cell></Section></List>;

  const u = data.user;
  const name =
    [u.last_name, u.first_name, u.middle_name].filter(Boolean).join(" ") ||
    `tg:${u.tg_user_id}`;

  return (
    <List>
      {feedback && (
        <Banner type="section" header={feedback.kind === "ok" ? "Готово" : "Ошибка"}>
          {feedback.text}
        </Banner>
      )}

      <Section header="Пилот">
        <Cell subtitle={u.tg_username ? `@${u.tg_username} · tg ${u.tg_user_id}` : `tg ${u.tg_user_id}`}
          after={<VerificationPill status={u.verification_status} until={u.verified_until} />}
          multiline
        >
          {name}
        </Cell>
        {u.skill_class && <Cell subtitle="Уровень">{u.skill_class}</Cell>}
        {u.phone && <Cell subtitle="Телефон">{u.phone}</Cell>}
        {u.fasr_id && <Cell subtitle="ID ФАСР (текстом)">{u.fasr_id}</Cell>}
      </Section>

      <Section header="Подтверждение профиля">
        {u.verification_note && (
          <Cell subtitle="Заметка верификации" multiline>{u.verification_note}</Cell>
        )}
        <div style={{ padding: "8px 16px", display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
          <Button mode="filled" size="m" disabled={busy || u.verification_status === "verified"}
            onClick={() => setVerification("verified", null, oneYearFromNow())}>
            ✅ Подтвердить на год
          </Button>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <Button mode="bezeled" size="m" disabled={busy || u.verification_status === "pending"} stretched
                onClick={() => setVerification("pending", null, null)}>
                ⏳ На проверку
              </Button>
            </div>
            <div style={{ flex: 1 }}>
              <Button mode="plain" size="m" disabled={busy} stretched
                onClick={() => {
                  const reason = window.prompt("Причина отказа:");
                  if (reason) setVerification("rejected", reason, null);
                }}>
                ✕ Отклонить
              </Button>
            </div>
          </div>
        </div>
        <Cell multiline>
          <Caption level="1">
            «Подтвердить» открывает /book на год — типовой срок для членов клуба.
            «На проверку» — пилоту приходит ⏳, он понимает, что заявка в очереди.
            «Отклонить» требует причину; пилот увидит её в DM.
          </Caption>
        </Cell>
      </Section>

      <Section header="Сообщение пилоту">
        {!dmOpen && (
          <div style={{ padding: "8px 16px" }}>
            <Button mode="bezeled" size="m" stretched disabled={busy}
              onClick={() => { setDmOpen(true); setFeedback(null); }}>
              ✉️ Написать пилоту
            </Button>
          </div>
        )}
        {dmOpen && (
          <>
            <Cell multiline>
              <Caption level="1">
                Бот отправит пилоту это сообщение в личку — удобно попросить
                догрузить FASR ID или уточнить срок страховки перед верификацией.
              </Caption>
            </Cell>
            <Cell>
              <Textarea
                placeholder="Здравствуйте! Подгрузите, пожалуйста, фото ID ФАСР — иначе не могу подтвердить заявку."
                value={dmText}
                onChange={(e) => setDmText(e.target.value)}
                rows={4}
              />
            </Cell>
            <Cell><Caption level="1">{dmText.length} / 4000</Caption></Cell>
            <div style={{ padding: "0 16px 8px", display: "flex", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <Button mode="filled" size="m" stretched
                  disabled={busy || dmText.trim().length === 0}
                  onClick={sendDm}>
                  {busy ? "Отправляем…" : "Отправить"}
                </Button>
              </div>
              <div style={{ flex: 1 }}>
                <Button mode="plain" size="m" stretched disabled={busy}
                  onClick={() => { setDmOpen(false); setDmText(""); }}>
                  Отмена
                </Button>
              </div>
            </div>
          </>
        )}
      </Section>

      <DocumentsBlock
        documents={data.documents}
        onReview={reviewDoc}
        busy={busy}
        slug={slug}
      />

      <Section header={`Предупреждения (${data.warnings.length})`}>
        {data.warnings.length === 0 && <Cell subtitle="История чистая.">Без предупреждений</Cell>}
        {data.warnings.map((w) => (
          <Cell key={w.id} subtitle={new Date(w.issued_at).toLocaleDateString("ru-RU")} multiline>
            {w.reason}
          </Cell>
        ))}
      </Section>

      <Section>
        <Cell onClick={() => navigate(backTo)}>{backLabel}</Cell>
      </Section>
    </List>
  );
}

function VerificationPill({ status, until }) {
  if (status === "verified") {
    return (
      <span className="rcf-pill rcf-pill--ok">
        ✓ {until ? `до ${until}` : "Подтверждён"}
      </span>
    );
  }
  if (status === "pending")  return <span className="rcf-pill rcf-pill--warn">⏳ На проверке</span>;
  if (status === "rejected") return <span className="rcf-pill rcf-pill--danger">✕ Отклонён</span>;
  return <span className="rcf-pill rcf-pill--neutral">— без статуса</span>;
}

function DocumentsBlock({ documents, onReview, busy, slug }) {
  if (documents.length === 0) {
    return (
      <Section header="Документы">
        <Cell><Caption level="1">Пилот ещё ничего не загрузил.</Caption></Cell>
      </Section>
    );
  }

  const pending = documents.filter((d) => d.status === "pending");
  const decided = documents.filter((d) => d.status !== "pending");

  return (
    <>
      <Section header={pending.length > 0
        ? `На подтверждение (${pending.length})`
        : "На подтверждение"}>
        {pending.length === 0 && (
          <Cell><Caption level="1">Новых документов на проверку нет.</Caption></Cell>
        )}
        {pending.map((d) => (
          <PendingDocRow key={d.id} doc={d} onReview={onReview} busy={busy} slug={slug} />
        ))}
      </Section>

      {decided.length > 0 && (
        <Section header={`История документов (${decided.length})`}>
          {decided.map((d) => (
            <DecidedDocRow key={d.id} doc={d} onReview={onReview} busy={busy} slug={slug} />
          ))}
        </Section>
      )}
    </>
  );
}

function docSubtitle(doc) {
  const parts = [
    `#${doc.id}`,
    `загружен ${new Date(doc.uploaded_at).toLocaleDateString("ru-RU")}`,
  ];
  if (doc.expires_on) parts.push(`до ${new Date(doc.expires_on).toLocaleDateString("ru-RU")}`);
  return parts.join(" · ");
}

function openDocFile(slug, docId) {
  return openAuthedFile(`/api/v1/admin/airfields/${slug}/documents/${docId}/file`)
    .catch((e) => window.alert(e.message || "Не удалось открыть файл."));
}

function PendingDocRow({ doc, onReview, busy, slug }) {
  const title = KIND_LABELS[doc.kind] || doc.kind;
  return (
    <>
      <Cell subtitle={docSubtitle(doc)} multiline>{title}</Cell>
      <div style={{ padding: "4px 16px 8px", display: "grid", gap: 8 }}>
        <Button mode="bezeled" size="s" stretched disabled={busy}
          onClick={() => openDocFile(slug, doc.id)}>
          📄 Открыть файл
        </Button>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1 }}>
            <Button mode="filled" size="s" stretched disabled={busy}
              onClick={() => onReview(doc.id, "verified", null)}>
              ✅ Подтвердить
            </Button>
          </div>
          <div style={{ flex: 1 }}>
            <Button mode="plain" size="s" stretched disabled={busy}
              onClick={() => {
                const reason = window.prompt("Причина отказа:");
                if (reason) onReview(doc.id, "rejected", reason);
              }}>
              ✕ Отклонить
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

function DecidedDocRow({ doc, onReview, busy, slug }) {
  const [open, setOpen] = useState(false);
  const status = DOC_STATUS_META[doc.status] || DOC_STATUS_META.pending;
  const title = KIND_LABELS[doc.kind] || doc.kind;
  return (
    <>
      <Cell
        onClick={() => setOpen(!open)}
        subtitle={docSubtitle(doc)}
        after={<span className={`rcf-pill rcf-pill--${status.tone}`}>{status.text}</span>}
        multiline
      >
        {title}
      </Cell>
      {open && (
        <div style={{ padding: "4px 16px 8px", display: "grid", gap: 8 }}>
          <Button mode="bezeled" size="s" stretched disabled={busy}
            onClick={() => openDocFile(slug, doc.id)}>
            📄 Открыть файл
          </Button>
          {doc.status === "verified" && (
            <Button mode="plain" size="s" stretched disabled={busy}
              onClick={() => {
                const reason = window.prompt("Причина отзыва (увидит пилот):");
                if (reason) onReview(doc.id, "rejected", reason);
              }}>
              ↺ Отозвать подтверждение
            </Button>
          )}
          {doc.status === "rejected" && (
            <Button mode="bezeled" size="s" stretched disabled={busy}
              onClick={() => onReview(doc.id, "verified", null)}>
              ✅ Всё же подтвердить
            </Button>
          )}
        </div>
      )}
    </>
  );
}

function oneYearFromNow() {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}
